import { Component } from '@angular/core';
import { DocumentState } from '@app/core/enums/document/document-state.enum';
import { DocumentType } from '@app/core/enums/document/document-type.enum';
import { Asset } from '@app/core/model/entities/asset/asset';
import { CreateDocumentInput, DocumentToCreateFromFile } from '@app/core/model/entities/document/document';
import {
  OrganizationDocumentCreateModalComponent
} from '@app/features/main/views/organization-documents/modals/organization-document-create-modal/organization-document-create-modal.component';
import { CreateDocumentData } from '@app/shared/components/file-uploader/file-uploader.component';
import dayjs from 'dayjs';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    templateUrl: './document-create-modal.component.html',
    standalone: false
})
export class DocumentCreateModalComponent extends OrganizationDocumentCreateModalComponent {

  constructor() {
    super();

    // Add columns that are not relevant for an Asset's Document
    this.propertiesGridColumnState = [
      {colId: 'defaultSortCol', sort: 'asc'},
      {colId: 'assets', hide: true},
      {colId: 'assetDocumentSection', hide: true},
      {colId: 'documentState', hide: true}
    ];
  }

  /**
   * Create a Document input for each row of the properties grid.
   * @return Document inputs.
   */
  public getCreateDocumentInputs(): CreateDocumentInput[] {
    const documentsToUpdate: CreateDocumentInput[] = [];
    if (this.gridApi) {
      this.gridApi.forEachNode(node => {
        documentsToUpdate.push({
          fileName: node.data.fileName,
          type: DocumentType.DOCUMENT,
          state: DocumentState.ACTIVE,
          name: node.data.document.name,
          entityId: this.uploadData.entityId,
          entityType: this.uploadData.entityType,
          properties: {
            // Use spread to add optional field if condition is fulfilled
            ...(node.data.document.documentCategory && {documentCategory: node.data.document.documentCategory}),
            ...(node.data.document.date && {
              documentDate: dayjs(node.data.document.date).format(this.appConfig.DATE_FORMAT)
            }),
            ...(node.data.document.description && {description: node.data.document.description})
          }
        });
      });
    }
    return documentsToUpdate;
  }

  /**
   * Fetch available categories for autocompletion in the Document properties grid. Assets are not fetched since they
   * are not used in this case.
   * @return Observable of the upload configuration.
   * @protected
   */
  protected fetchUploadConfiguration(): Observable<{ assets: Asset[], categories: string[] }> {
    return this.documentsService.availableDocumentCategories$
      .pipe(
        map(categories => {
          return {
            categories,
            assets: [] // Assets are not used in this modal and do not need to be fetched
          };
        }),
        takeUntilDestroyed(this.destroyRef)
      );
  }

  /**
   * Build an Asset's Document from an uploaded file's data.
   * @param documentData Uploaded file information to use for the Document's properties.
   * @return Data to use for creating a new Document.
   * @protected
   */
  protected buildDocumentFromFile(documentData: CreateDocumentData): DocumentToCreateFromFile {
    return new DocumentToCreateFromFile(documentData.fileName, documentData.documentName);
  }
}
